import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { THEME_IDS, isTheme, type Appearance, type ThemeId } from '@recipes/contracts';
import { useConfig } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { forgetOwnPhoto } from '@/lib/appearance/own-photo';
import { useAuth } from '@/lib/auth/auth-provider';
import {
  appearanceSnapshot,
  chooseAppearance,
  forgetPendingAppearance,
  reconcileAppearance,
  rememberInstallationTheme,
  subscribeAppearance,
  type AppearanceChoice,
} from '@/lib/appearance/appearance';

/** What is in effect on this device; re-renders when it changes, also by the operating system. */
export function useAppearance(): Appearance & { dark: boolean } {
  return useSyncExternalStore(subscribeAppearance, appearanceSnapshot, appearanceSnapshot);
}

/** The themes this instance offers; the build's list until the configuration is known. */
export function useEnabledThemes(): ThemeId[] {
  const { data } = useConfig();
  const enabled = data?.themes?.filter(isTheme);
  return enabled?.length ? enabled : [...THEME_IDS];
}

const profileAppearance = (user: Appearance): Appearance => ({
  theme: user.theme,
  colorMode: user.colorMode,
  backgroundStyle: user.backgroundStyle,
  backgroundDim: user.backgroundDim,
});

const saveToProfile = async (patch: AppearanceChoice): Promise<Appearance> =>
  profileAppearance(await api.users.updateProfile(patch));

/**
 * Keeps the device and the profile on the same look (appearance spec 6.5): after sign-in, when
 * the profile changes on another device, and when the connection returns. Rendered once by the
 * app shell, next to the language sync.
 */
export function useAppearanceSync(): void {
  const { status, user, setUser } = useAuth();
  const { data: config } = useConfig();
  const userId = user?.id;
  const key = user
    ? `${user.theme}|${user.colorMode}|${user.backgroundStyle}|${user.backgroundDim}`
    : null;

  useEffect(() => rememberInstallationTheme(config?.defaultTheme), [config?.defaultTheme]);

  useEffect(() => {
    if (status !== 'authenticated' || !user || !key) return;
    let cancelled = false;
    const run = () =>
      void reconcileAppearance(profileAppearance(user), saveToProfile, navigator.onLine).then(
        (saved) => {
          if (!cancelled && saved) setUser({ ...user, ...saved });
        },
      );
    run();
    window.addEventListener('online', run);
    return () => {
      cancelled = true;
      window.removeEventListener('online', run);
    };
    // `user` is read for the merge only; re-running on every user object would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, userId, key]);

  useEffect(() => {
    if (status !== 'anonymous') return;
    forgetPendingAppearance();
    // The look stays on the device; the private photo of whoever was signed in does not.
    void forgetOwnPhoto();
  }, [status]);
}

/**
 * The one way to change the look: applies at once, remembers it on the device and – signed in –
 * saves it to the profile now or when the connection is back.
 */
export function useChooseAppearance(): (patch: AppearanceChoice) => Promise<void> {
  const { status, user, setUser } = useAuth();
  return useCallback(
    async (patch) => {
      const signedIn = status === 'authenticated';
      chooseAppearance(patch, signedIn);
      if (!signedIn || !user) return;
      const saved = await reconcileAppearance(
        profileAppearance(user),
        saveToProfile,
        navigator.onLine,
      );
      if (saved) setUser({ ...user, ...saved });
    },
    [status, user, setUser],
  );
}
